import { ElementRef, inject } from "@angular/core";

declare global {
    interface Window {
        angular: any;
        angBridge: any;
    }

    interface HTMLElement {
        angBridge?: any;
    }
}

export interface AngularScope {
    /**
     * Returns an unsubscribe callback to stop listening to the event.
     * typical example:
     * ```ts
     * effect((onCleanup) => {
     *   const unsubscribe = $scope.$on("MyEvent", (_, data) => {...handle event...});
     *   onCleanup(() => unsubscribe());
     * }
     * ```
     */
    $on: (eventName: string, fn: (eventName: string, data: unknown) => void) => () => void;
    $emit: (eventName: string, data: any) => void;
}
export namespace AngBridgeUtil {
    // bridge to AngularJS; used for AngJS to set model values and call setter fns
    export function init(component: object) {
        inject(ElementRef).nativeElement.angBridge = component;
    }

    /**
     * Creates an AngularJS $scope bridge for emitting and listening to events.
     * Ideally, this would get replaced with an Angular-y store or service instead.
     * This functionality depends on an <s25-angular-wrapper> in your component's hierarchy.
     * typical example:
     * ```ts
     * readonly $scope = AngBridgeUtil.make$Scope();
     *
     * constructor() {
     *   effect((onCleanup) => {
     *     const unsubscribe = this.$scope.$on("MyEvent", (_, data) => {...handle event...});
     *     onCleanup(() => unsubscribe());
     *   });
     * }
     */
    export function make$Scope(this: void): AngularScope {
        const angularWrapper = findAngularWrapper();
        return {
            $on: (evtName: string, fn: (evtName: string, data: unknown) => void) => {
                return window.angular?.element(angularWrapper)?.scope()?.$on(evtName, fn);
            },
            $emit: (evtName: string, data: any) => {
                window.angular?.element(angularWrapper)?.scope()?.$emit(evtName, data);
            },
        };
    }

    /** Fetch an AngularJS service from `$injector`, once we're sure it's ready */
    export async function $inject(depName: string) {
        await window.angBridge?._injectorReady;
        return $inject.sync(depName);
    }

    export namespace $inject {
        /** Optimistically skip the wait for angBridge to be ready */
        export function sync(depName: string) {
            return window.angBridge?.$injector?.get(depName);
        }
    }

    function findAngularWrapper() {
        let angularWrapper = inject(ElementRef).nativeElement;
        while (angularWrapper && angularWrapper.tagName !== "S25-ANGULAR-WRAPPER") {
            angularWrapper = angularWrapper?.parentNode;
        }
        if (!angularWrapper) {
            throw new Error("Could not find <s25-angular-wrapper>");
        }
        return angularWrapper;
    }
}

export class AngBridge {
    constructor() {
        AngBridgeUtil.init(this);
    }
}
